"use client";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import * as React from "react";
import { BaseChartProps } from "./types";

interface ChartWrapperProps
  extends Pick<
    BaseChartProps,
    | "title"
    | "description"
    | "footer"
    | "className"
    | "containerClassName"
    | "config"
    | "height"
  > {
  children: React.ReactNode;
  /** Show card wrapper */
  showCard?: boolean;
  /** Additional header actions */
  headerActions?: React.ReactNode;
  /** Center header content */
  centerHeader?: boolean;
  /** Metric value to display prominently */
  metricValue?: string;
  /** Metric change indicator (e.g., "+201% from last month") */
  metricChange?: string;
  /** Show metric layout */
  showMetricLayout?: boolean;
}

export const ChartWrapper: React.FC<ChartWrapperProps> = ({
  title,
  description,
  footer,
  className,
  containerClassName,
  config,
  height,
  children,
  showCard = true,
  headerActions,
  centerHeader = false,
  metricValue,
  metricChange,
  showMetricLayout = false,
}) => {
  const chartContent = (
    <ChartContainer
      config={config}
      className={cn(
        height ? `min-h-[${height}px]` : "min-h-[350px]",
        "w-full h-full",
        containerClassName,
      )}
    >
      {children as React.ReactElement}
    </ChartContainer>
  );

  if (!showCard && !title && !description && !footer) {
    return chartContent;
  }

  return (
    <Card className={cn("flex flex-col h-full rounded-lg", className)}>
      {(title || description || headerActions || showMetricLayout) && (
        <CardHeader
          className={cn(
            centerHeader && "items-center pb-0",
            !centerHeader &&
              "flex flex-row items-start justify-between space-y-0 pb-4",
            showMetricLayout && "pb-2",
          )}
        >
          <div className={cn("space-y-1", centerHeader && "text-center")}>
            {title && (
              <CardTitle
                className={cn(
                  // showMetricLayout &&
                  "text-sm font-medium text-muted-foreground",
                )}
              >
                {title}
              </CardTitle>
            )}
            {showMetricLayout && metricValue && (
              <div className="text-3xl font-bold">{metricValue}</div>
            )}
            {showMetricLayout && metricChange && (
              <p className="text-xs text-muted-foreground">{metricChange}</p>
            )}
            {description && !showMetricLayout && (
              <CardDescription>{description}</CardDescription>
            )}
          </div>
          {headerActions && !centerHeader && (
            <div className="flex items-center gap-2">{headerActions}</div>
          )}
        </CardHeader>
      )}
      <CardContent
        className={cn("flex-1 overflow-hidden", footer ? "pb-0" : "pb-4")}
      >
        {chartContent}
      </CardContent>
      {footer && (
        <CardFooter className="flex-col gap-2 text-sm">{footer}</CardFooter>
      )}
    </Card>
  );
};

ChartWrapper.displayName = "ChartWrapper";
